import type {
	AppID,
	CartValidation,
	NubeFormActionEventData,
	NubeFormFileGetEventData,
	NubeFormFileGetResponseEventData,
	NubeIframeMessageEventData,
	NubeNavigateEventData,
	NubeSDKRuntime,
	NubeSDKSendableEvent,
	NubeScrollToEventData,
	NubeSdkInternalEvent,
	NubeSdkInternalEventData,
	NubeStorageGetResponseEventData,
	NubeStorageId,
	NubeStorageQueryEventData,
	NubeStorageRemoveResponseEventData,
	NubeStorageSetEventData,
	NubeStorageSetResponseEventData,
	WorkerInternalMessage,
	WorkerMessage,
	WorkerMessageHandlerFunc,
} from "@tiendanube/nube-sdk-internal-types";
import { getNavigateHandler } from "./navigate";
import { handleSlotSet } from "./slot-set";
import { handleTelemetry } from "./telemetry";
import { reportDevtoolsLog } from "./telemetry/devtools.ts";
import type { WorkerTelemetryMessage } from "./telemetry/telemetry.ts";

export function parseCartValidation(value: unknown): CartValidation {
	const val = value as CartValidation | undefined;

	if (!val) return { status: "pending" };

	switch (val.status) {
		case "fail":
			if (typeof val.reason === "string")
				return { status: "fail", reason: val.reason };
			break;

		case "success":
			return { status: "success" };
	}

	return { status: "pending" };
}

export const handleCartAddCoupon: WorkerMessageHandlerFunc = (
	{ changes },
	state,
) => {
	const newCode = changes?.cart?.coupon?.code;
	const currentCode = state.cart?.coupon?.code;

	if (newCode === undefined || newCode === currentCode) {
		return null;
	}

	return {
		cart: {
			coupon: {
				code: newCode,
			},
		},
	};
};

export const handleCartRemoveCoupon: WorkerMessageHandlerFunc = (
	{ changes },
	state,
) => {
	const currentCode = state.cart?.coupon?.code;
	return !currentCode || changes ? null : {};
};

export const handleAccountRegister: WorkerMessageHandlerFunc = ({
	changes,
}) => ({
	customer: {
		name: changes?.customer?.name,
		email: changes?.customer?.email,
		phone: changes?.customer?.phone,
		cpf_cnpj: changes?.customer?.cpf_cnpj,
	},
});

export const handleShippingSelection: WorkerMessageHandlerFunc = (
	{ changes },
	{ shipping },
) => {
	if (changes?.shipping?.selected === shipping?.selected) return null;

	const selectedOption = changes?.shipping?.selected;

	if (!selectedOption) return null;

	const hasValidOption = Boolean(
		shipping?.options?.find((option) => option.code === selectedOption),
	);

	if (!hasValidOption) return null;

	return {
		shipping: {
			options: shipping?.options || [],
			custom_labels: shipping?.custom_labels || {},
			selected: selectedOption,
		},
	};
};

export const handleConfigSet: WorkerMessageHandlerFunc = ({
	app,
	changes,
}) => ({
	apps: {
		[app]: {
			config: {
				handle_cart_before_update:
					changes?.config?.handle_cart_before_update || false,
				has_cart_validation: changes?.config?.has_cart_validation || false,
				disable_shipping_more_options:
					changes?.config?.disable_shipping_more_options || false,
			},
		},
	},
});

/** `AppError` comes off the worker wire, so its shape is not guaranteed. */
function describeAppError(error: unknown): string {
	if (typeof error === "string") return error;
	if (typeof error === "object" && error !== null) {
		const { code, message } = error as { code?: unknown; message?: unknown };
		if (typeof message === "string" && message) return message;
		if (typeof code === "string" && code) return code;
	}
	return "Unknown worker error";
}

export const handleWebWorkerError: WorkerMessageHandlerFunc = (
	{ app, error },
	state,
) => {
	// The app failed to load, or the register could not process one of its
	// messages: the most severe failure there is, and the one the app never
	// gets to log itself.
	if (error)
		reportDevtoolsLog(app, "ERROR", "worker-error", describeAppError(error));

	const currentErrors = state.apps?.[app]?.errors || [];
	const newErrors = error ? [...currentErrors, error] : currentErrors;

	return {
		apps: {
			[app]: {
				errors: newErrors,
			},
		},
	};
};

export const handleCartValidation: WorkerMessageHandlerFunc = ({
	app,
	changes,
}) => ({
	apps: {
		[app]: {
			cart: {
				validation: parseCartValidation(changes?.cart?.validation),
			},
		},
	},
});

export const handleOrderExtra: WorkerMessageHandlerFunc = (
	{ changes },
	{ order },
) => {
	const extra = changes?.order?.extra;

	if (!extra) return null;

	return {
		order: { ...order, extra },
	};
};

const handlers = new Map<
	NubeSDKSendableEvent | "WebWorkerError",
	WorkerMessageHandlerFunc
>([
	["config:set", handleConfigSet],
	["cart:validate", handleCartValidation],
	["coupon:add", handleCartAddCoupon],
	["coupon:remove", handleCartRemoveCoupon],
	["shipping:select", handleShippingSelection],
	["ui:slot:set", handleSlotSet],
	["account:register", handleAccountRegister],
	["order:add:extra", handleOrderExtra],
	["WebWorkerError", handleWebWorkerError],
]);

/**
 * Removes the `apps` branch from a worker's `changes` payload, returning the
 * original object untouched when there is nothing to remove (the common case,
 * so no allocation).
 */
function stripApps(changes: WorkerMessage["changes"]) {
	if (!changes || !("apps" in changes)) return changes;
	const { apps, ...rest } = changes as WorkerMessage["changes"] & {
		apps?: unknown;
	};
	return rest as WorkerMessage["changes"];
}

export function createWorkerMessageHandler(
	handlers: Map<
		NubeSDKSendableEvent | "WebWorkerError",
		WorkerMessageHandlerFunc
	>,
) {
	return (message: WorkerMessage, runtime: NubeSDKRuntime) => {
		const { app, type } = message;
		// `apps` is owned by the main thread. Workers see it in their state, so
		// an app echoing its state back could overwrite another app's entry.
		// Drop it here so only the explicit handlers below write that branch.
		const changes = stripApps(message.changes);
		const sanitized =
			changes === message.changes ? message : { ...message, changes };
		const handler = handlers.get(type);

		if (handler) {
			const state = runtime.getState();
			const result = handler(sanitized, state);
			if (result) {
				runtime.send(app, type, () => result);
			}
			return;
		}

		if (changes) {
			runtime.send(app, type, () => changes);
			return;
		}

		runtime.send(app, type);
	};
}

export const workerMessageHandler = createWorkerMessageHandler(handlers);

export type WorkerInternalMessageOptions = {
	localStorage: Storage;
	sessionStorage: Storage;
};

export function buildAppStorageKey(appId: AppID, key: string) {
	return `app-${appId}-${key}`;
}

export function encodeAppStorageValue(
	value: string | null,
	ttl?: number,
	now: number = Date.now(),
) {
	const data = {
		value,
		...(ttl !== undefined ? { ttl: now + ttl * 1000 } : {}),
	};
	return JSON.stringify(data);
}

export function decodeAppStorageValue(
	storageValue: string | null,
	now = Date.now(),
): string | null {
	if (!storageValue) return null;
	try {
		const data = JSON.parse(storageValue);

		if (!data) {
			return null;
		}

		if (typeof data !== "object") {
			console.error(
				"Invalid value stored in storage, maybe it was modified from outside the SDK",
				storageValue,
			);
			return null;
		}

		if (data?.ttl) {
			// Has expiration date, validate
			if (typeof data.ttl !== "number") {
				console.error(
					"Invalid value stored in storage, maybe it was modified from outside the SDK",
					storageValue,
				);
				return null;
			}
			const isExpired = !!data.ttl && data.ttl < now;
			if (isExpired) return null;
		}

		if (data.value && typeof data.value !== "string") {
			console.error(
				"Invalid value stored in storage, maybe it was modified from outside the SDK",
				storageValue,
			);
			return null;
		}

		if (data.value === undefined) return null;

		return data.value;
	} catch (err) {
		console.error(
			"Invalid value stored in storage, maybe it was modified from outside the SDK",
			storageValue,
			err,
		);
		return null;
	}
}

export function isInternalUrl(
	url: string,
	baseUrl: string,
): {
	status: boolean;
	error?: string;
} {
	try {
		if (url.startsWith("http://") || url.startsWith("https://")) {
			const urlObj = new URL(url);
			const baseUrlObj = new URL(baseUrl);

			if (urlObj.hostname !== baseUrlObj.hostname) {
				return {
					status: false,
					error: "External URL detected",
				};
			}

			if (
				urlObj.pathname === baseUrlObj.pathname &&
				urlObj.search === baseUrlObj.search
			) {
				return {
					status: false,
					error: "Already on target URL",
				};
			}

			return { status: true };
		}

		if (!url.startsWith("/") && !url.startsWith("./")) {
			return {
				status: false,
				error: "Invalid relative URL format",
			};
		}

		const normalizedUrl = new URL(url, baseUrl);
		const normalizedBaseUrl = new URL(baseUrl);

		if (
			normalizedUrl.pathname === normalizedBaseUrl.pathname &&
			normalizedUrl.search === normalizedBaseUrl.search
		) {
			return {
				status: false,
				error: "Already on target URL",
			};
		}

		return { status: true };
	} catch {
		return {
			status: false,
			error: "Invalid URL format",
		};
	}
}

export function createWorkerTelemetryMessageHandler(): (
	sessionId: string,
	message: WorkerTelemetryMessage,
) => void {
	return (sessionId: string, message: WorkerTelemetryMessage) => {
		handleTelemetry(sessionId, message);
	};
}

export function createWorkerInternalMessageHandler(
	appId: AppID,
	worker: Worker,
	options: WorkerInternalMessageOptions,
): (message: WorkerInternalMessage, runtime: NubeSDKRuntime) => void {
	function getStorage(storageId: NubeStorageId) {
		switch (storageId) {
			case "session-storage":
				return options.sessionStorage;
			case "local-storage":
				return options.localStorage;
		}
	}

	function postMessage(
		eventId: NubeSdkInternalEvent,
		data: NubeSdkInternalEventData,
	) {
		worker.postMessage(
			JSON.stringify({
				type: eventId,
				data: data,
			}),
		);
	}

	function isEventStorageQueryEvent(event: WorkerInternalMessage): boolean {
		return (
			event.type === "internal:storage:get" ||
			event.type === "internal:storage:set" ||
			event.type === "internal:storage:remove"
		);
	}

	function isNavigateEvent(event: WorkerInternalMessage): boolean {
		return event.type === "internal:navigate";
	}

	function isIframeMessageEvent(event: WorkerInternalMessage): boolean {
		return event.type === "internal:iframe:message";
	}

	function isFormActionEvent(event: WorkerInternalMessage): boolean {
		return (
			event.type === "internal:form:submit" ||
			event.type === "internal:form:reset"
		);
	}

	/**
	 * Ownership check.
	 *
	 * `__internalId`s follow the pattern `<componentType>-<appId>-<hash>`
	 * (see `generateInternalId` in `@tiendanube/nube-sdk-ui`). The
	 * leading `<componentType>` segment is NOT a stable identifier — it
	 * comes from the factory's `.name` and differs between the unstyled
	 * `formRoot()` factory (`"formRoot"`) and `styled(Form.Root)`
	 * (`"FormRoot"`, from the JSX wrapper's function name). Pinning the
	 * check on the prefix string was the bug — other event-driven
	 * components (`Field`, `Select`, `Button`, …) never inspect the id
	 * prefix, they just key off `state.ui.values[__internalId]`.
	 *
	 * The reliable ownership signal is the `<appId>` segment between the
	 * type prefix and the trailing hash. We anchor the regex to the end
	 * of the id and only require the segment to match `message.app` —
	 * the same shape `generateInternalId` itself uses when reusing
	 * inbound ids. The DOM `instanceof HTMLFormElement` check below is
	 * what enforces "this id resolves to a form".
	 */
	function ownsForm(formId: string, owner: AppID): boolean {
		const escapedOwner = owner.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
		return new RegExp(`-${escapedOwner}-[a-z0-9]+$`).test(formId);
	}

	/**
	 * Resolves an `<Iframe>` by its SDK-owned `__internalId`.
	 *
	 * The renderer stores it in `data-nube-iframe-id` because a public `id`
	 * prop overrides the DOM id. Falls back to `getElementById` for adapters
	 * still bundling a renderer that predates the attribute.
	 */
	function findIframeByInternalId(iframeId: string): Element | null {
		for (const el of document.querySelectorAll("[data-nube-iframe-id]")) {
			if (el.getAttribute("data-nube-iframe-id") === iframeId) {
				return el;
			}
		}
		return document.getElementById(iframeId);
	}

	function isFormFileEvent(event: WorkerInternalMessage): boolean {
		return event.type === "internal:form:file:get";
	}

	/**
	 * Returns the file selected in the form's `<input type="file">` whose
	 * `name` matches, read straight from the DOM so it always reflects the
	 * current selection (cleared on reset, replaced on re-pick).
	 */
	function findSelectedFile(formId: string, fieldName: string): File | null {
		const form = document.getElementById(formId);
		if (!(form instanceof HTMLFormElement)) return null;

		for (const element of form.elements) {
			if (
				element instanceof HTMLInputElement &&
				element.type === "file" &&
				element.name === fieldName
			) {
				return element.files?.[0] ?? null;
			}
		}
		return null;
	}

	function isScrollToEvent(event: WorkerInternalMessage): boolean {
		return event.type === "internal:page:scroll_to";
	}

	return (message, runtime) => {
		if (isNavigateEvent(message)) {
			const data = message.data as NubeNavigateEventData;
			const route = data.route;

			const navigateHandler = getNavigateHandler(route);
			if (navigateHandler) {
				navigateHandler.handler({ appId, runtime });
				return;
			}

			const { url } = runtime.getState().location;

			const result = isInternalUrl(route, url);
			if (result.status) {
				window.location.href = route;
				return;
			}

			reportDevtoolsLog(
				appId,
				"WARN",
				"navigate",
				`Navigation to "${route}" ignored: ${result.error}.`,
			);

			runtime.send("checkout", "WebWorkerError", () => ({
				apps: {
					[appId]: {
						errors: [
							...(runtime.getState().apps?.[appId]?.errors || []),
							{
								code: "navigation_error",
								message: result.error,
							},
						],
					},
				},
			}));
		}

		if (isEventStorageQueryEvent(message)) {
			const data = message.data as NubeStorageQueryEventData;
			const storage = getStorage(data.storageId);
			const storageKey = buildAppStorageKey(appId, data.key);

			switch (message.type) {
				case "internal:storage:get": {
					const storageValue = storage.getItem(storageKey);
					const value = decodeAppStorageValue(storageValue);
					const response: NubeStorageGetResponseEventData = {
						storageId: data.storageId,
						key: data.key,
						requestId: data.requestId,
						value,
					};
					postMessage("internal:storage:get:response", response);
					break;
				}

				case "internal:storage:set": {
					const setEventData = data as NubeStorageSetEventData;
					const finalValue = encodeAppStorageValue(
						setEventData.value,
						setEventData.ttl,
					);
					try {
						storage.setItem(storageKey, finalValue);
					} catch (err) {
						// Setting an item in storage can fail if the storage is full, we log the error but we don't propagate it to the worker.. maybe
						// this should be propagated?
						console.error(err);
					}
					const response: NubeStorageSetResponseEventData = {
						storageId: data.storageId,
						requestId: data.requestId,
					};
					postMessage("internal:storage:set:response", response);
					break;
				}

				case "internal:storage:remove": {
					storage.removeItem(storageKey);
					const response: NubeStorageRemoveResponseEventData = {
						storageId: data.storageId,
						requestId: data.requestId,
					};
					postMessage("internal:storage:remove:response", response);
					break;
				}
			}
		}

		if (isIframeMessageEvent(message)) {
			const data = message.data as NubeIframeMessageEventData;
			const iframe = findIframeByInternalId(data.iframeId);
			const src = data.src;

			if (!iframe) {
				const warning = `postMessageToIframe ignored: iframe "${data.iframeId}" is not mounted. Wait for the iframe to signal it is ready before posting.`;
				console.warn(`[nube-sdk] ${warning}`);
				reportDevtoolsLog(appId, "WARN", "iframe", warning);
				return;
			}

			let targetOrigin: string | undefined;
			if (src) {
				try {
					targetOrigin = new URL(src).origin;
				} catch (error) {
					console.error("Error handling worker message: Invalid URL", {
						src,
						error,
					});
					reportDevtoolsLog(
						appId,
						"ERROR",
						"iframe",
						`postMessageToIframe ignored: "${src}" is not a valid URL.`,
					);
					targetOrigin = undefined;
				}
			}

			if (
				iframe instanceof HTMLIFrameElement &&
				iframe.contentWindow &&
				targetOrigin
			) {
				let iframeOrigin: string | undefined;
				if (iframe.src) {
					try {
						iframeOrigin = new URL(iframe.src).origin;
					} catch (error) {
						console.error("Error handling worker message: Invalid iframe URL", {
							iframeSrc: iframe.src,
							error,
						});
					}
				}

				if (targetOrigin !== iframeOrigin) {
					console.error(
						"Error handling worker message: Target origin mismatch",
						{
							expected: iframeOrigin,
							got: targetOrigin,
							iframeId: data.iframeId,
						},
					);
					reportDevtoolsLog(
						appId,
						"ERROR",
						"iframe",
						`postMessageToIframe ignored: target origin "${targetOrigin}" does not match iframe "${data.iframeId}" origin "${iframeOrigin}".`,
					);
					return;
				}

				iframe.contentWindow.postMessage(data.message, targetOrigin);
			}
		}

		if (isFormActionEvent(message)) {
			const data = message.data as NubeFormActionEventData;
			const { formId } = data;

			// Cross-app attempts are dropped — the event must address a form
			// owned by the same app that dispatched it. Logged so that an
			// honest mismatch (e.g. wrong appId in the formId prefix)
			// surfaces in the console instead of looking like dead code.
			if (!ownsForm(formId, message.app)) {
				const error = `Form action "${message.type}" ignored: app "${message.app}" does not own form "${formId}".`;
				console.error(`[nube-sdk] ${error}`);
				reportDevtoolsLog(appId, "ERROR", "form", error);
				return;
			}

			const form = document.getElementById(formId);
			if (!(form instanceof HTMLFormElement)) {
				return;
			}

			if (message.type === "internal:form:submit") {
				// `requestSubmit()` triggers the form's `submit` event, which is
				// already wired to `Form.Root`'s React handler — so validation,
				// fetch, and the JSON-encoded onChange payload all run as if a
				// `Form.Submitter` had been clicked.
				form.requestSubmit();
			} else {
				// `reset()` fires the native `reset` event, which `Form.Root`
				// listens to in order to walk the field-level reset hooks.
				form.reset();
			}
		}

		if (isFormFileEvent(message)) {
			const data = message.data as NubeFormFileGetEventData;
			const response: NubeFormFileGetResponseEventData = {
				requestId: data.requestId,
				file: null,
			};

			if (ownsForm(data.formId, message.app)) {
				response.file = findSelectedFile(data.formId, data.fieldName);
			} else {
				console.error(
					`[nube-sdk] Form action "${message.type}" ignored: app "${message.app}" does not own form "${data.formId}".`,
				);
			}

			try {
				// Posted as an object (not a JSON string) so the `File` goes
				// through structured clone and reaches the worker intact.
				worker.postMessage({
					type: "internal:form:file:get:response",
					data: response,
				});
			} catch (err) {
				// Still answer, so the worker's `getFormFile` Promise can't hang.
				console.error("[nube-sdk] Failed to post form file response:", err);
				worker.postMessage({
					type: "internal:form:file:get:response",
					data: { requestId: data.requestId, file: null },
				});
			}
		}

		if (isScrollToEvent(message)) {
			const data = message.data as NubeScrollToEventData;
			window.scrollTo({
				top: data.top,
				left: data.left,
				behavior: data.behavior,
			});
		}
	};
}

export function createWorker(...parts: string[]): Worker {
	const blob = new Blob(parts, { type: "application/javascript" });
	const worker = new Worker(URL.createObjectURL(blob));
	return worker;
}
